
import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { ArrowDown, MoreHorizontal, Edit2, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

interface Recebimento {
  id: string;
  descricao: string;
  valor: number;
  dataVencimento?: Date;
  dataPagamento?: Date;
  cliente: string;
  status: string;
}

interface RecebimentosListProps {
  recebimentos: Recebimento[];
  onEdit: (recebimento: Recebimento) => void;
  onDelete: (recebimento: Recebimento) => void;
  onReceber?: (recebimento: Recebimento) => void;
  tipoLista: "pendentes" | "historico";
}

const RecebimentosList: React.FC<RecebimentosListProps> = ({
  recebimentos,
  onEdit,
  onDelete,
  onReceber,
  tipoLista
}) => {
  // Renderiza o menu de ações (editar e excluir) para cada item
  const renderAcoesMenu = (recebimento: Recebimento) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <span className="sr-only">Abrir menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onEdit(recebimento)}>
          <Edit2 className="mr-2 h-4 w-4" />
          Editar
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => onDelete(recebimento)} 
          className="text-red-600"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Excluir
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (recebimentos.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-4">
      {recebimentos.map((recebimento) => {
        const isPendente = tipoLista === "pendentes";
        const borderColorClass = isPendente
          ? recebimento.status === 'atrasado' ? 'border-l-red-500' : 'border-l-yellow-500'
          : 'border-l-green-500';

        return (
          <Card key={recebimento.id} className={`border-l-4 ${borderColorClass}`}>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row justify-between">
                <div className="space-y-1">
                  <h4 className="font-medium">{recebimento.descricao}</h4>
                  <p className="text-sm text-gray-500">Cliente: {recebimento.cliente}</p>
                  <p className="text-sm">
                    {isPendente ? 'Vencimento: ' : 'Recebido em: '}
                    {format(
                      isPendente ? recebimento.dataVencimento! : recebimento.dataPagamento!, 
                      "dd 'de' MMMM 'de' yyyy", 
                      { locale: pt }
                    )}
                    {recebimento.status === 'atrasado' && isPendente && (
                      <span className="ml-2 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                        Atrasado
                      </span>
                    )}
                    {recebimento.status === 'pago' && !isPendente && (
                      <span className="ml-2 text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                        Pago
                      </span>
                    )}
                  </p>
                </div>
                <div className="mt-4 md:mt-0 flex flex-col md:items-end">
                  <div className="flex justify-end items-center">
                    <p className={`text-lg font-semibold mr-2 ${!isPendente ? 'text-green-600' : ''}`}>
                      {recebimento.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </p>
                    {renderAcoesMenu(recebimento)}
                  </div>
                  {isPendente && onReceber ? (
                    <Button 
                      className="mt-2 bg-green-600 hover:bg-green-700"
                      size="sm"
                      onClick={() => onReceber(recebimento)}
                    >
                      <ArrowDown className="h-3 w-3 mr-1" /> Registrar Recebimento
                    </Button>
                  ) : !isPendente ? (
                    <Button 
                      className="mt-2"
                      size="sm"
                      variant="outline"
                    >
                      Ver Detalhes
                    </Button>
                  ) : null}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default RecebimentosList;
